'use client';

import { ToggleGroup } from 'radix-ui';
import { type ReactNode, useState } from 'react';

import { tv } from '../../lib/tv';

export type SegmentedControlProps = {
  /** Controlled selected value. */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  /** Called with the newly selected value (never empty - one is always selected). */
  onValueChange?: (value: string) => void;
  /** Names the group, e.g. "Billing period". */
  'aria-label': string;
  children: ReactNode;
  className?: string;
};

export type SegmentedControlItemProps = {
  value: string;
  /** Label, plus optional extras such as a `<Badge size="sm" tone="brand">`. */
  children: ReactNode;
  className?: string;
  /**
   * Every press, including one on the item that's already selected (which
   * doesn't change the value) - e.g. to reopen a "Custom" range picker.
   */
  onClick?: () => void;
};

/**
 * Figma "Billing toggle" (90:362, Landing Page ↳ Pricing). Track: bg/muted,
 * radius/full, space/1 padding and gap. Item: radius/full, 8px × 20px, gap
 * space/2, Label/Medium. Selected: bg/surface + Shadow/XS, text/primary.
 * Unselected: no fill, text/secondary (text/primary on hover).
 */
export const segmentedControlStyles = tv({
  slots: {
    root: 'bg-bg-muted inline-flex w-fit items-center gap-1 rounded-full p-1',
    item: [
      'text-label-md text-text-secondary inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full px-5 py-2 whitespace-nowrap outline-none',
      'transition-[color,background-color,box-shadow] duration-[120ms] ease-out',
      'hover:text-text-primary',
      'data-[state=on]:bg-bg-surface data-[state=on]:text-text-primary data-[state=on]:shadow-xs',
      // Important: beats the selected item's shadow-xs.
      'focus-visible:shadow-focus-ring!',
    ],
  },
});

/**
 * Pick one of a few mutually exclusive views or options in place (billing
 * period, chart range). Radix ToggleGroup type="single": items are radios
 * (`aria-checked`), arrow keys move focus, Space/Enter select. Selection
 * can't be cleared.
 */
export function SegmentedControl({
  value,
  defaultValue,
  onValueChange,
  children,
  className,
  ...props
}: SegmentedControlProps) {
  const [inner, setInner] = useState(defaultValue ?? '');
  const selected = value ?? inner;
  const styles = segmentedControlStyles();
  return (
    <ToggleGroup.Root
      type="single"
      data-slot="segmented-control"
      value={selected}
      onValueChange={(next) => {
        // Radix clears the value when the selected item is pressed again: ignore.
        if (!next) return;
        setInner(next);
        onValueChange?.(next);
      }}
      className={styles.root({ className })}
      {...props}
    >
      {children}
    </ToggleGroup.Root>
  );
}

export function SegmentedControlItem({
  value,
  children,
  className,
  onClick,
}: SegmentedControlItemProps) {
  return (
    <ToggleGroup.Item
      value={value}
      onClick={onClick}
      data-slot="segmented-control-item"
      className={segmentedControlStyles().item({ className })}
    >
      {children}
    </ToggleGroup.Item>
  );
}
